import { MoneyCollectOutlined, PoweroffOutlined, UserOutlined } from '@ant-design/icons';
import { App, Form, Input } from 'antd';
import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { usePrevious } from 'react-use';
import { useRequest } from '../../../api/hooks';
import Modal from '@/components/Modal';
import PosButton from '@/components/PosButton';
import { getCardPrice, WriteOffCardUserList } from '@/models/card';
import { DateTimeFormat } from '@/models/common';
import { Coupon, getCouponPrice } from '@/models/coupon';
import { hasDeliveryType, OrderDeliveryType, OrderType, PreOrder } from '@/models/order';
import { codePayTradeCancel, PaymentChannel, ReceivingMode } from '@/models/payment';
import { PointRules } from '@/models/point';
import {
  calcSpecificationsDeliveryType,
  calcSpecificationsTotalPrice,
  POS_ENDPOINT,
  POSPaymentResponse,
} from '@/models/pos';
import { GOODS_TYPE } from '@/models/sku';
import { UserFullInfoContext } from '@/models/user';
import { playSound } from '@/utils/sound';
import { getUserAvatar } from '@/utils/uiHelper';
import BindUser from '../FastActions/BindUser';
import Payment from './paymentArea/payment';
import { pointToAmount } from './PointConsume';
import styles from './style.less';
import CardTradeArea from './tradeArea/card';
import CartTradeArea from './tradeArea/cart';
import DirectTradeArea from './tradeArea/direct';
import RechargeTradeArea from './tradeArea/recharge';
import { timestampStr } from '@/utils/common';

export type SettlementProps = {
  onClose: (hasChange?: boolean) => void;
  mode: (typeof ReceivingMode)[keyof typeof ReceivingMode];
  onReceived: (orderCompleteInfo: POSPaymentResponse) => void;
  // order?: Order;
  // specifications?: SKUSpecification[];

  preOrder?: PreOrder;
  pointRules: PointRules;
  // onRecharge: BindUserProps['onRecharge'];
  // onBindUserChange?: BindUserProps['onBind'];
};

/**
 * 收单
 *
 * @param onClose
 * @param defaultVisible
 * @constructor
 */
const ReceivingOrderModal: React.FC<SettlementProps> = ({
  onClose,
  mode,
  // order,
  preOrder,
  onReceived,
  pointRules,
}: // onBindUserChange,
// onRecharge,
SettlementProps) => {
  const { userFullInfo, updateUserFullInfo } = useContext(UserFullInfoContext);
  // const { categoryList, pointRules, cardList } = useContext(POSBaseInfoContext);
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const prevMode = usePrevious(mode);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon>(null);
  const [selectedCards, setSelectedCards] = useState<{ [cardUserId: number]: number }>(null);
  const {
    payload,
    send,
    loading,
    error,
    reset: resetPayload,
    clearErrorAndLoading,
  } = useRequest<POSPaymentResponse>(
    {
      url: `${POS_ENDPOINT}/trade/pay`,
      method: 'post',
    },
    {
      manual: true,
    }
  );

  const reset = () => {
    // resetPayload();
    clearErrorAndLoading();
    form.resetFields();
    setSelectedCoupon(null);
    setSelectedCards(null);
  };

  useEffect(() => {
    setOpen(mode !== ReceivingMode.INVALID);
    if (prevMode !== undefined) {
      reset();
    }
  }, [mode]);

  const setPrices = () => {
    if (mode === ReceivingMode.DIRECT || mode === ReceivingMode.RECHARGE) {
      form.setFieldsValue({
        // paymentChannel: PaymentChannel.Online,
        deliveryType: OrderDeliveryType.INVALID,
      });
    } else if (mode === ReceivingMode.CART && preOrder && preOrder.specifications) {
      // 交付类型
      const deliveryType = calcSpecificationsDeliveryType(preOrder.specifications);
      const hasServiceDeliveryType = hasDeliveryType(deliveryType, OrderDeliveryType.IN_STORE_SERVICE);

      // 普通商品结算
      const goodsPrice = calcSpecificationsTotalPrice(preOrder.specifications);

      // 预付卡
      let { cardPrice, writeOffCardUserList } = getCardPrice({
        specifications: preOrder.specifications,
        userFullInfo: preOrder.userFullInfo,
        selectedCards: selectedCards,
      });

      // 优惠券
      const couponPrice = selectedCoupon
        ? // 抵扣预付卡金额后，再计算优惠券价格
          getCouponPrice(selectedCoupon, goodsPrice.minus(cardPrice))
        : new BigNumber(0);

      const pointPrice = pointToAmount(form.getFieldValue('pointNum'), pointRules);

      const beforePointPrice = goodsPrice.minus(couponPrice).minus(cardPrice);

      // 余额支付金额
      const balancePrice = form.getFieldValue('balancePrice')
        ? new BigNumber(form.getFieldValue('balancePrice'))
        : new BigNumber(0);

      const beforeTotalPrice = beforePointPrice.minus(pointPrice);

      // 付款总额: 客户视角 - 本次订单实际支出(包含钱包余额)
      const totalPrice = beforeTotalPrice.minus(
        // 减去抹零金额
        new BigNumber(form.getFieldValue('waiverAmount') || 0)
      );
      // 实付款: 商户视角 - 本次订单实际收入金额(不含钱包余额)
      const actualPrice = totalPrice
        // .minus(new BigNumber(form.getFieldValue('waiverAmount') || 0))
        .minus(balancePrice);

      form.setFieldsValue({
        // paymentChannel: PaymentChannel.Online,
        goodsPrice,
        cardPrice: cardPrice.comparedTo(0) > 0 ? cardPrice : undefined,
        writeOffCardUserList: cardPrice.comparedTo(0) > 0 ? writeOffCardUserList : undefined,
        couponPrice: couponPrice.comparedTo(0) > 0 ? couponPrice : undefined,
        pointPrice: pointPrice.comparedTo(0) > 0 ? pointPrice : undefined,
        balancePrice: balancePrice.comparedTo(0) > 0 ? balancePrice : undefined,
        beforePointPrice: beforePointPrice,
        beforeTotalPrice: beforeTotalPrice,
        actualPrice: actualPrice,
        totalPrice: totalPrice,
        waiverAmount: form.getFieldValue('waiverAmount'),
        deliveryType: deliveryType,
        serviceStartTime: hasServiceDeliveryType ? preOrder.addTime : undefined,
        serviceEndTime: hasServiceDeliveryType ? dayjs().format(DateTimeFormat) : undefined,
        appointmentId: preOrder.appointment?.id,
      });
    } else if (mode === ReceivingMode.CARD && preOrder && preOrder.cards) {
      // 预付卡结算
      const goodsPrice = preOrder.cards.reduce((acc, cur) => {
        return acc.plus(new BigNumber(cur.price).multipliedBy(1));
      }, new BigNumber(0));

      form.setFieldsValue({
        // paymentChannel: PaymentChannel.Online,
        goodsPrice,
        actualPrice: goodsPrice,
        deliveryType: OrderDeliveryType.INVALID,
      });
    }
  };

  useEffect(() => {
    setPrices();
  }, [preOrder, mode, selectedCoupon, selectedCards]);

  const onSubmit = values => {
    if (loading) return;

    message.destroy();
    if (!values.paymentChannel) {
      message.error('请选择收款方式');
      return;
    }

    if (mode === ReceivingMode.DIRECT && !values.actualPrice) {
      message.error('请输入收款金额');
      return;
    }

    if (values.paymentChannel === PaymentChannel.Online && !values.authCode) {
      message.error('请扫描或输入客户付款码');
      return;
    }

    if (mode === ReceivingMode.CART) {
      values.orderType = OrderType.Normal;
      // 普通商品
      const specifications = preOrder.specifications;
      values.cartList = specifications.map(v => ({
        id: v.goodsId,
        name: v.goodsName,
        type: v.goodsType,
        specificationId: v.id,
        specificationName: v.name,
        specificationCost: v.cost,
        number: v.number,
        price: v.price,
        picUrl: v.picUrl || v.goodsPicUrl,
      }));

      // 待核销预付卡
      if (values.writeOffCardUserList) {
        values.writeOffCardUserList = (values.writeOffCardUserList as WriteOffCardUserList).map(card => ({
          cardUserId: card.cardUserId,
          number: card.number,
          specificationId: card.specificationId,
        }));
      }

      // 美容师
      if (preOrder.operator) {
        values.operatorUserName = preOrder.operator.username;
      }
    } else if (mode === ReceivingMode.CARD) {
      values.orderType = OrderType.Card;
      // 购买预付卡
      values.cartList = preOrder.cards.map(card => ({
        id: card.id,
        name: card.name,
        type: GOODS_TYPE.CARD,
        specificationId: 0,
        number: 1,
        price: card.price,
      }));
    } else if (mode === ReceivingMode.RECHARGE) {
      values.orderType = OrderType.Recharge;
      // 充值
      values.rechargePrice = values.actualPrice;
    } else if (mode === ReceivingMode.DIRECT) {
      values.orderType = OrderType.Pay;
    }

    // 优惠券
    if (selectedCoupon) {
      values.couponUserId = selectedCoupon.id;
      values.couponId = selectedCoupon.cid;
    }

    // 关联客户
    try {
      values.userId = userFullInfo.user.id;
    } catch (e) {}

    const loadingMsgKey = `pay-${timestampStr()}`;
    message.loading({
      key: loadingMsgKey,
      content: '收款中，请稍等',
      duration: 0,
    });

    send({
      retry: 5,
      data: values,
    })
      .then(rsp => {
        setOpen(false);
        playSound('money');
        try {
          onReceived(rsp);
        } catch (e) {}
      })
      .catch(v => {
        playSound('error');
        message.error('下单失败: ' + v.msg);
      })
      .finally(() => {
        message.destroy(loadingMsgKey);
      });
  };

  return (
    <>
      {mode !== ReceivingMode.INVALID && (
        <Modal
          className={styles.modal}
          maskClosable={false}
          style={{ top: 20 }}
          styles={{ body: { padding: 0 } }}
          width={1050}
          open={open}
          destroyOnHidden
          closable={false}
          afterClose={() => {
            onClose();
          }}
          onCancel={() => {
            setOpen(false);
          }}
          footer={null}>
          <>
            <div className={styles.modalTitle}>
              <div className={styles.modalTitleMain}>
                <MoneyCollectOutlined /> 结算 & 收银
              </div>
              <div className={styles.modalTitleUser}>
                <BindUser
                  userFullInfo={userFullInfo}
                  renderButton={userFullInfo => {
                    return (
                      <PosButton type={'primary'} size={'middle'} shape={'round'}>
                        {userFullInfo ? (
                          <span>
                            {getUserAvatar(userFullInfo.user, {
                              size: 20,
                              style: { marginRight: 5 },
                            })}
                            <span>{userFullInfo.user.nickname}</span> <span>{userFullInfo.user.mobile}</span>
                          </span>
                        ) : (
                          <>
                            <UserOutlined /> 绑定该客户
                          </>
                        )}
                      </PosButton>
                    );
                  }}
                />
              </div>
              <PosButton
                size={'middle'}
                loading={loading}
                danger
                type="primary"
                icon={<PoweroffOutlined />}
                onClick={() => {
                  setOpen(false);
                }}>
                取消
              </PosButton>
            </div>
            <Form
              initialValues={{ paymentChannel: PaymentChannel.Online }}
              form={form}
              size={'large'}
              onValuesChange={changedValues => {
                if (changedValues.hasOwnProperty('pointNum')) {
                  setPrices();
                }
                if (changedValues.hasOwnProperty('balancePrice')) {
                  setPrices();
                }
                if (changedValues.hasOwnProperty('waiverAmount')) {
                  setPrices();
                }
              }}
              onFinishFailed={({ errorFields }) => {
                if (errorFields.length) {
                  playSound('error');
                  message.destroy();
                  errorFields.forEach(errorField => {
                    errorField.errors.forEach(error => {
                      message.error(error);
                    });
                  });
                }
              }}
              onFinish={onSubmit}>
              <div className={styles.container}>
                <div className={styles.tradeArea}>
                  {mode === ReceivingMode.RECHARGE && <RechargeTradeArea userFullInfo={userFullInfo} form={form} />}

                  {mode === ReceivingMode.DIRECT && <DirectTradeArea form={form} />}

                  {mode !== ReceivingMode.DIRECT && mode !== ReceivingMode.RECHARGE && (
                    <>
                      <Form.Item name={'actualPrice'} noStyle>
                        <Input type={'hidden'} />
                      </Form.Item>
                    </>
                  )}

                  {mode === ReceivingMode.CARD && <CardTradeArea form={form} cards={preOrder.cards} />}

                  {mode === ReceivingMode.CART && (
                    <CartTradeArea
                      form={form}
                      preOrder={preOrder}
                      pointRules={pointRules}
                      selectedCoupon={selectedCoupon}
                      onSelectedCouponChange={selected => {
                        setSelectedCoupon(selected);
                      }}
                      selectedCards={selectedCards}
                      onSelectedCardsChange={(card, number) => {
                        setSelectedCards(selected => {
                          return Object.assign({}, selected, { [card.id]: number });
                        });
                      }}
                      balancePayAvailable={mode === ReceivingMode.CART}
                    />
                  )}
                </div>

                <div className={styles.paymentArea}>
                  <Payment
                    form={form}
                    user={userFullInfo}
                    requestLoading={loading}
                    requestError={error}
                    // onRecharge={onRecharge}
                    balancePayAvailable={mode === ReceivingMode.CART}
                    onRequestCodePayTradeCancel={async payInfo => {
                      await codePayTradeCancel(payInfo);
                      message.success('撤销成功，请等待交易状态更新');
                    }}
                  />

                  <Form.Item name={'goodsPrice'} noStyle>
                    <Input type={'hidden'} />
                  </Form.Item>
                  <Form.Item name={'couponPrice'} noStyle>
                    <Input type={'hidden'} />
                  </Form.Item>
                  <Form.Item name={'waiverAmount'} noStyle>
                    <Input type={'hidden'} />
                  </Form.Item>
                  <Form.Item name={'cardPrice'} noStyle>
                    <Input type={'hidden'} />
                  </Form.Item>
                  <Form.Item name={'writeOffCardUserList'} noStyle>
                    <Input type={'hidden'} />
                  </Form.Item>
                  <Form.Item name={'pointPrice'} noStyle>
                    <Input type={'hidden'} />
                  </Form.Item>
                  <Form.Item name={'balancePrice'} noStyle>
                    <Input type={'hidden'} />
                  </Form.Item>
                  <Form.Item name={'deliveryType'} noStyle>
                    <Input type={'hidden'} />
                  </Form.Item>
                  <Form.Item name={'serviceStartTime'} noStyle>
                    <Input type={'hidden'} />
                  </Form.Item>
                  <Form.Item name={'serviceEndTime'} noStyle>
                    <Input type={'hidden'} />
                  </Form.Item>
                  <Form.Item name={'appointmentId'} noStyle>
                    <Input type={'hidden'} />
                  </Form.Item>
                </div>
              </div>
            </Form>
          </>
        </Modal>
      )}
    </>
  );
};

export default ReceivingOrderModal;
